import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// Always use relative paths — dev proxy handles /api → localhost:8000,
// production FastAPI serves both the API and the built frontend from the same origin.
const BACKEND_URL = '';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);

  const api = useCallback(async (endpoint, options = {}) => {
    const headers = { 'Content-Type': 'application/json', ...options.headers };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`${BACKEND_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }

    return res.json();
  }, [token]);

  const checkAuth = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return null;
    }

    try {
      const userData = await api('/api/auth/me');
      setUser(userData);
      return userData;
    } catch (err) {
      localStorage.removeItem('auth_token');
      setToken(null);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [api, token]);

  const login = async (email, password) => {
    const data = await api('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    localStorage.setItem('auth_token', data.token);
    setToken(data.token);
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    const data = await api('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    });

    localStorage.setItem('auth_token', data.token);
    setToken(data.token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api('/api/auth/logout', { method: 'POST' });
    } catch (e) {}
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
  };

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      checkAuth,
      api,
      isAuthenticated: !!user,
      isOwner: user?.role === 'owner',
      isAdmin: ['owner', 'admin'].includes(user?.role),
      isManager: ['owner', 'admin', 'manager'].includes(user?.role),
    }}>
      {children}
    </AuthContext.Provider>
  );
};
